import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Heading, Text, useStyleConfig} from '@chakra-ui/react'
import InstaStorySlide from './partials/insta-story-slide'
import SanitizedHtml from '../../html-sanitizer'

const DEFAULT_SLIDE_DURATION = 5000 // ms

export const PlpInstaStory = ({
    heading,
    bodycopy,
    slidesGroup = [],
    autoplayDelay = DEFAULT_SLIDE_DURATION,
    uid,
}) => {
    const styles = useStyleConfig('PlpInstaStory')
    const [activeIndex, setActiveIndex] = useState(0)
    const [progress, setProgress] = useState(0)
    const timerRef = useRef(null)
    const progressRef = useRef(null)
    const startTimeRef = useRef(null)
    const animFrameRef = useRef(null)
    const pointerStartXRef = useRef(null)
    const pointerStartYRef = useRef(null)
    const pointerCurrentXRef = useRef(null)
    const pointerCurrentYRef = useRef(null)
    const pointerIdRef = useRef(null)
    const touchStartXRef = useRef(null)
    const touchStartYRef = useRef(null)
    const lastSwipeAtRef = useRef(0)

    const SWIPE_THRESHOLD = 40
    const SWIPE_CLICK_SUPPRESSION_MS = 300

    const slideDuration = typeof autoplayDelay === 'number' ? autoplayDelay : DEFAULT_SLIDE_DURATION
    const slideOffset = `translate3d(-${activeIndex * 100}%, 0, 0)`

    const resetAutoPlay = () => {
        cancelAnimationFrame(animFrameRef.current)
        clearTimeout(timerRef.current)
    }

    const handleSwipeByDelta = (deltaX, deltaY) => {
        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
            return false
        }

        lastSwipeAtRef.current = Date.now()
        resetAutoPlay()

        if (deltaX < 0) {
            goNext()
            return true
        }

        goToSlide(activeIndex - 1)
        return true
    }

    const clearPointerState = () => {
        pointerStartXRef.current = null
        pointerStartYRef.current = null
        pointerCurrentXRef.current = null
        pointerCurrentYRef.current = null
        pointerIdRef.current = null
    }

    const goToSlide = useCallback(
        (index) => {
            const clamped = Math.min(Math.max(index, 0), slidesGroup.length - 1)
            setActiveIndex(clamped)
            setProgress(0)
            startTimeRef.current = null
        },
        [slidesGroup.length],
    )

    const goNext = useCallback(() => {
        setActiveIndex((prev) => {
            const next = prev < slidesGroup.length - 1 ? prev + 1 : 0
            setProgress(0)
            startTimeRef.current = null
            return next
        })
    }, [slidesGroup.length])

    // Animate progress bar
    useEffect(() => {
        if (!slidesGroup.length) return

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp
            const elapsed = timestamp - startTimeRef.current
            const pct = Math.min((elapsed / slideDuration) * 100, 100)
            setProgress(pct)

            if (pct < 100) {
                animFrameRef.current = requestAnimationFrame(animate)
            } else {
                // auto advance
                timerRef.current = setTimeout(() => {
                    setActiveIndex((prev) => {
                        const next = prev < slidesGroup.length - 1 ? prev + 1 : 0
                        setProgress(0)
                        startTimeRef.current = null
                        return next
                    })
                }, 0)
            }
        }

        animFrameRef.current = requestAnimationFrame(animate)

        return () => {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
        }
    }, [activeIndex, slideDuration, slidesGroup.length])

    const handleClick = () => {
        if (Date.now() - lastSwipeAtRef.current < SWIPE_CLICK_SUPPRESSION_MS) {
            lastSwipeAtRef.current = 0
            return
        }

        resetAutoPlay()
        goNext()
    }

    const handlePointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return

        pointerIdRef.current = e.pointerId
        pointerStartXRef.current = e.clientX
        pointerStartYRef.current = e.clientY
        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY

        if (e.currentTarget?.setPointerCapture) {
            e.currentTarget.setPointerCapture(e.pointerId)
        }
    }

    const handlePointerMove = (e) => {
        if (pointerIdRef.current !== e.pointerId) return

        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY
    }

    const handlePointerUp = (e) => {
        if (pointerIdRef.current !== e.pointerId) {
            return
        }

        if (pointerStartXRef.current === null || pointerStartYRef.current === null) {
            pointerIdRef.current = null
            return
        }

        const endX = pointerCurrentXRef.current ?? e.clientX
        const endY = pointerCurrentYRef.current ?? e.clientY
        const deltaX = endX - pointerStartXRef.current
        const deltaY = endY - pointerStartYRef.current

        clearPointerState()

        if (e.currentTarget?.releasePointerCapture) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId)
            } catch (error) {
                // ignore capture release failures
            }
        }

        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleTouchStart = (e) => {
        const touch = e.touches?.[0]
        if (!touch) return

        touchStartXRef.current = touch.clientX
        touchStartYRef.current = touch.clientY
    }

    const handleTouchEnd = (e) => {
        const touch = e.changedTouches?.[0]
        if (!touch || touchStartXRef.current === null || touchStartYRef.current === null) {
            return
        }

        const deltaX = touch.clientX - touchStartXRef.current
        const deltaY = touch.clientY - touchStartYRef.current

        touchStartXRef.current = null
        touchStartYRef.current = null

        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleTouchCancel = () => {
        touchStartXRef.current = null
        touchStartYRef.current = null
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
        }
        if (e.key === 'ArrowRight') {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
            goNext()
        }
        if (e.key === 'ArrowLeft') {
            cancelAnimationFrame(animFrameRef.current)
            clearTimeout(timerRef.current)
            goToSlide(activeIndex - 1)
        }
    }

    if (!slidesGroup || slidesGroup.length === 0) return null

    return (
        <Box
            {...styles.container}
            data-testid="plp-insta-story"
            id={uid ? `plp-insta-story-${uid}` : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={clearPointerState}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            role="group"
            tabIndex={0}
            aria-label={`Insta story slide ${activeIndex + 1} of ${slidesGroup.length}`}
        >
            <Flex {...styles.contentContainer}>
                {/* Progress dashes */}
                <Flex {...styles.progressBar} aria-hidden="true">
                    {slidesGroup.map((_, idx) => {
                        const barWidth =
                            idx < activeIndex ? '100%' : idx === activeIndex ? `${progress}%` : '0%'
                        return (
                            <Box key={idx} {...styles.progressTrack}>
                                <Box
                                    {...styles.progressFill}
                                    ref={idx === activeIndex ? progressRef : undefined}
                                    style={{width: barWidth}}
                                />
                            </Box>
                        )
                    })}
                </Flex>

                {(heading || bodycopy) && (
                    <Box {...styles.headerContainer}>
                        {heading && (
                            <Heading as="h2" {...styles.heading}>
                                {heading}
                            </Heading>
                        )}
                        {bodycopy && (
                            <SanitizedHtml as={Text} html={bodycopy} {...styles.bodycopy} />
                        )}
                    </Box>
                )}
            </Flex>

            <Box {...styles.slideTrack} style={{transform: slideOffset}}>
                {slidesGroup.map((slide, idx) => (
                    <Box key={idx} {...styles.slide}>
                        <InstaStorySlide
                            button={slide?.button}
                            image={slide?.image}
                            styles={styles}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

PlpInstaStory.propTypes = {
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    uid: PropTypes.string,
    autoplayDelay: PropTypes.number,
    slidesGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            button: PropTypes.object,
            image: PropTypes.object,
        }),
    ),
}
